/**
 * The one Knowledge article renderer shared by Portal and Console.
 *
 * Plain `body` remains the complete search/MCP/legacy authority. An optional
 * `bodyDocument` may add semantic structure, but only after the runtime parser
 * accepts every node in the finite version-1 vocabulary. A future or malformed
 * document falls back as a whole to `body`; it is never partially interpreted.
 * React text children are the only authored values rendered here. There is no
 * HTML/Markdown parser and no authored value becomes a URL or attribute.
 */
import { Fragment, type ReactNode } from 'react'
import { parseArticleBody } from './articleTokens'
import {
  kbDocumentHeadingId, kbInlineText, parseKbDocument,
  type KbDocumentBlock, type KbInlineNode,
} from './document'
import { KbFigure, KbMissingFigure } from './KbFigure'
import { KbDocumentCard } from './KbDocumentCard'
import type { KbAsset } from './types'
import s from './kb.module.css'

export interface KbArticleBodyProps {
  articleId: string
  body: string | null | undefined
  /** Optional versioned finite document. Invalid/future values fail closed to body. */
  bodyDocument?: unknown
  assets: KbAsset[]
  assetsLoading?: boolean
  assetsFailed?: boolean
  requestUrl: (input: { articleId: string; assetKey: string }) => Promise<string>
  openDocument?: (url: string) => void
}

export function KbArticleBody({
  articleId, body, bodyDocument, assets, assetsLoading = false, assetsFailed = false,
  requestUrl, openDocument,
}: KbArticleBodyProps) {
  const figures = new Map(
    assets.filter((asset) => asset.kind === 'figure').map((asset) => [asset.asset_key, asset]))
  const documents = assets.filter((asset) => asset.kind === 'document')
  const document = parseKbDocument(bodyDocument)
  const rejectedDocument = bodyDocument !== null && bodyDocument !== undefined && !document

  // While the manifest is arriving, a marker gets the loading state instead
  // of briefly claiming that a figure is missing.
  const known = assetsLoading
    ? new Set(parseArticleBody(body, []).flatMap((segment) => segment.kind === 'missing' ? [segment.id] : []))
    : new Set(figures.keys())

  let figureNumber = 0
  const renderFigure = (id: string, key: string, index: number) => {
    const asset = figures.get(id)
    if (!asset && assetsLoading) {
      return (
        <div key={key} className={s.skeleton} role="status" aria-busy="true"
          aria-label="Loading this figure" data-testid={`kb-figure-loading-${id}`} />
      )
    }
    if (!asset) return <KbMissingFigure key={key} id={id} />
    return <KbFigure key={key} articleId={articleId} asset={asset}
      requestUrl={requestUrl} index={index} />
  }

  const renderBlocks = (blocks: KbDocumentBlock[], path = 'b'): ReactNode[] => blocks.map((block, index) => {
    const key = `${path}-${index}`
    if (block.type === 'paragraph') {
      return <p key={key} className={s.prose}>{renderInline(block.content, key)}</p>
    }
    if (block.type === 'heading') {
      const text = kbInlineText(block.content)
      const id = path === 'b' ? kbDocumentHeadingId(text, index) : undefined
      if (block.level === 2) return <h2 key={key} id={id} className={s.heading}>{renderInline(block.content, key)}</h2>
      if (block.level === 3) return <h3 key={key} id={id} className={s.heading}>{renderInline(block.content, key)}</h3>
      return <h4 key={key} id={id} className={s.heading}>{renderInline(block.content, key)}</h4>
    }
    if (block.type === 'divider') return <hr key={key} className={s.divider} />
    if (block.type === 'figure') {
      figureNumber += 1
      return renderFigure(block.figure_id, `${key}-${block.figure_id}`, figureNumber)
    }
    if (block.type === 'list') {
      const List = block.ordered ? 'ol' : 'ul'
      return (
        <List key={key} className={s.richList}>
          {block.items.map((item, itemIndex) => (
            <li key={`${key}-i-${itemIndex}`}>{renderBlocks(item.blocks, `${key}-i-${itemIndex}`)}</li>
          ))}
        </List>
      )
    }
    if (block.type === 'callout') {
      return (
        <aside key={key} className={s.calloutBox} data-tone={block.tone} role="note"
          aria-label={block.tone === 'warning' ? 'Warning' : block.tone === 'info' ? 'Information' : 'Note'}>
          {renderBlocks(block.blocks, `${key}-c`)}
        </aside>
      )
    }
    return (
      <div key={key} className={s.tableScroll} tabIndex={0} role="region"
        aria-label="Scrollable article table">
        <table className={s.richTable} aria-label="Article reference table">
          <thead><tr>{block.header.map((cell, cellIndex) => (
            <th key={`${key}-h-${cellIndex}`} scope="col">{renderInline(cell, `${key}-h-${cellIndex}`)}</th>
          ))}</tr></thead>
          <tbody>{block.rows.map((row, rowIndex) => (
            <tr key={`${key}-r-${rowIndex}`}>{row.map((cell, cellIndex) => (
              <td key={`${key}-r-${rowIndex}-${cellIndex}`}>{renderInline(cell, `${key}-r-${rowIndex}-${cellIndex}`)}</td>
            ))}</tr>
          ))}</tbody>
        </table>
      </div>
    )
  })

  return (
    <div className={s.article} data-testid="kb-article-body">
      {rejectedDocument && (
        <div className={s.state} role="status" data-testid="kb-document-fallback">
          <p className={s.stateText}>
            This article’s structured layout isn’t available in this version. The complete text is shown below.
          </p>
        </div>
      )}

      {document ? renderBlocks(document.blocks) : parseArticleBody(body, known).map((segment, index) => {
        if (segment.kind === 'text') return <p key={`t${index}`} className={s.prose}>{segment.text}</p>
        if (segment.kind === 'missing') return <KbMissingFigure key={`m${index}-${segment.id}`} id={segment.id} />
        figureNumber += 1
        return renderFigure(segment.id, `f${index}-${segment.id}`, figureNumber)
      })}

      {documents.length > 0 && (
        <section className={s.docList} aria-labelledby="kb-attachments">
          <h3 id="kb-attachments" className={s.docTitle}>Attached documents</h3>
          {documents.map((documentAsset) => (
            <KbDocumentCard key={documentAsset.asset_key} articleId={articleId}
              asset={documentAsset} requestUrl={requestUrl} open={openDocument} />
          ))}
        </section>
      )}

      {assetsFailed && (
        <div className={s.state} role="status" data-testid="kb-assets-failed">
          <p className={s.stateText}>
            The figures and documents for this article couldn’t be loaded. The text
            above is complete — try reloading the page, and if it keeps happening,
            raise a support case.
          </p>
        </div>
      )}
    </div>
  )
}

function renderInline(nodes: KbInlineNode[], key: string): ReactNode {
  return nodes.map((node, index) => {
    const nodeKey = `${key}-n-${index}`
    if (node.type === 'text') return <Fragment key={nodeKey}>{node.text}</Fragment>
    if (node.type === 'line_break') return <br key={nodeKey} />
    if (node.type === 'strong') return <strong key={nodeKey}>{renderInline(node.content, nodeKey)}</strong>
    return <em key={nodeKey}>{renderInline(node.content, nodeKey)}</em>
  })
}
